import { useMemo } from "react";
import { Check, X } from "lucide-react";

/**
 * Lightweight password strength meter — no external deps.
 *
 * Scoring (0–4):
 *   0 empty · 1 weak · 2 fair · 3 good · 4 strong
 *
 * Weight breakdown:
 *   +1 length ≥ 8
 *   +1 length ≥ 12
 *   +1 has upper & lower & digit (character variety)
 *   +1 has symbol AND length ≥ 12
 *   −1 penalty if password is all-numeric, all-lowercase, or matches user's email/name
 *
 * This is intentionally simpler than zxcvbn — good enough for KYC-tier friction
 * without shipping a 400KB dictionary to every visitor.
 */
const LEVELS = [
    { label: "—",       tone: "bg-muted",          text: "text-muted-foreground" },
    { label: "Weak",    tone: "bg-red-500",        text: "text-red-600 dark:text-red-400" },
    { label: "Fair",    tone: "bg-amber-500",      text: "text-amber-600 dark:text-amber-400" },
    { label: "Good",    tone: "bg-blue-500",       text: "text-blue-600 dark:text-blue-400" },
    { label: "Strong",  tone: "bg-emerald-500",    text: "text-emerald-600 dark:text-emerald-400" },
];

export function scorePassword(pw, disallow = []) {
    if (!pw) return { score: 0, checks: {} };
    const len = pw.length;
    const hasUpper = /[A-Z]/.test(pw);
    const hasLower = /[a-z]/.test(pw);
    const hasDigit = /\d/.test(pw);
    const hasSymbol = /[^A-Za-z0-9]/.test(pw);

    let score = 0;
    if (len >= 8) score += 1;
    if (len >= 12) score += 1;
    if (hasUpper && hasLower && hasDigit) score += 1;
    if (hasSymbol && len >= 12) score += 1;

    // Penalties
    if (/^\d+$/.test(pw) || /^[a-z]+$/.test(pw)) score = Math.max(1, score - 1);
    // Contains part of the user's email local-part or name — very common typo password
    const disallowLc = disallow.filter(Boolean).map((s) => s.toLowerCase());
    const pwLc = pw.toLowerCase();
    for (const bad of disallowLc) {
        if (bad.length >= 3 && pwLc.includes(bad)) {
            score = Math.max(1, score - 1);
            break;
        }
    }
    // Repeats / sequences penalty
    if (/(.)\1{2,}/.test(pw) || /(0123|1234|2345|3456|4567|5678|6789|abcd|qwer)/i.test(pw)) {
        score = Math.max(1, score - 1);
    }

    return {
        // Floor to 1 for any non-empty input so weak passwords always render
        // as "Weak" (1 red bar) instead of the neutral empty state.
        score: Math.max(pw ? 1 : 0, Math.min(4, score)),
        checks: { len8: len >= 8, len12: len >= 12, variety: hasUpper && hasLower && hasDigit, symbol: hasSymbol },
    };
}

export default function PasswordStrengthMeter({ password = "", disallow = [] }) {
    const { score, checks } = useMemo(() => scorePassword(password, disallow), [password, disallow]);
    const level = LEVELS[score];

    return (
        <div className="mt-2" data-testid="password-strength">
            <div className="flex gap-1" data-testid="password-strength-bars">
                {[1, 2, 3, 4].map((i) => (
                    <div
                        key={i}
                        className={`h-1.5 flex-1 rounded transition-colors duration-200 ${score >= i ? level.tone : "bg-muted"}`}
                        data-testid={`password-strength-bar-${i}`}
                    />
                ))}
            </div>
            <div className="flex items-center justify-between mt-1.5">
                <span className={`text-[11px] font-bold uppercase tracking-wider ${level.text}`} data-testid="password-strength-label">
                    {password ? level.label : "Enter a password"}
                </span>
                <div className="flex gap-2 text-[10px] text-muted-foreground">
                    <Rule ok={checks.len8}     label="8+" />
                    <Rule ok={checks.variety}  label="Aa1" />
                    <Rule ok={checks.len12}    label="12+" />
                    <Rule ok={checks.symbol}   label="#!" />
                </div>
            </div>
        </div>
    );
}

function Rule({ ok, label }) {
    return (
        <span className={`inline-flex items-center gap-0.5 ${ok ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground/70"}`}>
            {ok ? <Check className="w-2.5 h-2.5" /> : <X className="w-2.5 h-2.5" />} {label}
        </span>
    );
}
